/**
 * PipelinePayoutResults — the per-output result panels for the payoutSheets
 * stage (channel-sheet summaries + legacy-XLSX mirror, Wasserman, The Team,
 * Erica Heyward, Night Co, Skara Payments Tracker). Rendered inside the Payout
 * sheets stage row when the stage has a current result.
 */

import React from 'react';
import { PipelineStage } from '../../services/adminApi';

interface PipelinePayoutResultsProps {
  payout: PipelineStage;
}

const fmtUsd = (n: unknown): string =>
  typeof n === 'number' ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '';

const PipelinePayoutResults: React.FC<PipelinePayoutResultsProps> = ({ payout }) => {
  // result is only current when the stage is completed — during a rerun
  // ('running') or after a failed full run the persisted result is stale.
  if (payout?.status !== 'completed') return null;
  const result = (payout.result as any) || {};
  const css = result.channelSheetSummary;
  const w = result.wassermanSheet;
  const t = result.teamSheet;
  const e = result.ericaSheet;
  const n = result.nightcoSheet;
  const sk = result.skaraSheet;

  const panels: React.ReactNode[] = [];

  // Channel sheets + legacy-XLSX mirror
  if (css) {
    const summaryErrs: string[] = css.errors || [];
    const mirrorErrs: string[] = css.mirrorErrors || [];
    const issueCount = summaryErrs.length + mirrorErrs.length;
    // Only show the panel when there's something an admin should act on.
    if (issueCount > 0 || (css.mirrorSkipped || 0) > 0 || (css.skipped || 0) > 0) {
      panels.push(
        <div key="css" style={styles.mirrorPanel}>
          <div style={styles.mirrorHeader}>
            Channel sheets: {css.updated} updated, {css.skipped} skipped
            {' · '}Legacy-XLSX mirror: {css.mirrored} refreshed, {css.mirrorSkipped} skipped,{' '}
            <span style={issueCount > 0 ? styles.failedHint : undefined}>{mirrorErrs.length} errors</span>
          </div>
          {issueCount > 0 && (
            <details style={styles.mirrorDetails}>
              <summary style={styles.mirrorSummary}>
                {issueCount} channel issue{issueCount === 1 ? '' : 's'} — review, then re-run “Tracker summaries” from the Step re-runs panel
              </summary>
              <ul style={styles.mirrorList}>
                {summaryErrs.map((err: string, i: number) => (
                  <li key={`e${i}`} style={styles.mirrorErrItem}>{err}</li>
                ))}
                {mirrorErrs.map((err: string, i: number) => (
                  <li key={`m${i}`} style={styles.mirrorErrItem}>XLSX mirror — {err}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      );
    }
  }

  // Wasserman AdSense report
  if (w) {
    panels.push(
      <div key="wasserman" style={styles.mirrorPanel}>
        <div style={styles.mirrorHeader}>
          {w.success ? (
            <>
              Wasserman AdSense report: <strong>{w.tabName}</strong> — {w.channelCount} channels, {fmtUsd(w.grandTotal)}
              {w.sheetUrl && (
                <>
                  {' · '}
                  <a href={w.sheetUrl} target="_blank" rel="noreferrer" style={styles.link}>Open</a>
                </>
              )}
            </>
          ) : (
            <span style={styles.failedHint}>
              Wasserman AdSense report failed — {w.error}. Re-run it from the Step re-runs panel.
            </span>
          )}
        </div>
      </div>
    );
  }

  // The Team Direct Sold report
  if (t) {
    panels.push(
      <div key="team" style={styles.mirrorPanel}>
        <div style={styles.mirrorHeader}>
          {t.success ? (
            <>
              The Team DS report: <strong>{t.tabName}</strong> — {t.channelCount} channels, {fmtUsd(t.grandTotal)}
              {t.sheetUrl && (
                <>
                  {' · '}
                  <a href={t.sheetUrl} target="_blank" rel="noreferrer" style={styles.link}>Open</a>
                </>
              )}
            </>
          ) : (
            <span style={styles.failedHint}>
              The Team DS report failed — {t.error}. Re-run it from the Step re-runs panel.
            </span>
          )}
        </div>
      </div>
    );
  }

  // Erica Heyward report (DIRECT SALES tab + GOOGLE EARNINGS month block)
  if (e) {
    panels.push(
      <div key="erica" style={styles.mirrorPanel}>
        <div style={styles.mirrorHeader}>
          {e.success ? (
            <>
              Erica Heyward: <strong>{e.directSalesTab}</strong> — {e.directSalesChannelCount} channels, {fmtUsd(e.directSalesTotal)}
              {' · '}Google Earnings {e.googleEarningsMonth}
              {e.googleEarningsSkipped ? ' (skipped — month not at the front)' : ` — ${fmtUsd(e.googleEarningsTotal)}`}
              {e.sheetUrl && (
                <>
                  {' · '}
                  <a href={e.sheetUrl} target="_blank" rel="noreferrer" style={styles.link}>Open</a>
                </>
              )}
            </>
          ) : (
            <span style={styles.failedHint}>
              Erica Heyward report failed — {e.error}. Re-run it from the Step re-runs panel.
            </span>
          )}
        </div>
      </div>
    );
  }

  // Night Co report (two-section DIRECT SALES tab + GOOGLE EARNINGS month block)
  if (n) {
    panels.push(
      <div key="nightco" style={styles.mirrorPanel}>
        <div style={styles.mirrorHeader}>
          {n.success ? (
            <>
              Night Co: <strong>{n.directSalesTab}</strong> — {n.directSalesChannelCount} channels, {fmtUsd(n.directSalesTotal)}
              {' · '}Google Earnings {n.googleEarningsMonth}
              {n.googleEarningsSkipped ? ' (skipped — month not at the front)' : ` — ${fmtUsd(n.googleEarningsTotal)}`}
              {n.sheetUrl && (
                <>
                  {' · '}
                  <a href={n.sheetUrl} target="_blank" rel="noreferrer" style={styles.link}>Open</a>
                </>
              )}
            </>
          ) : (
            <span style={styles.failedHint}>
              Night Co report failed — {n.error}. Re-run it from the Step re-runs panel.
            </span>
          )}
        </div>
      </div>
    );
  }

  // Skara Payments Tracker (front month block on the "Payments Tracker" tab)
  if (sk) {
    const missing: string[] = sk.unmatchedChannels || [];
    panels.push(
      <div key="skara" style={styles.mirrorPanel}>
        <div style={styles.mirrorHeader}>
          {sk.success ? (
            <>
              Skara Payments Tracker: <strong>{sk.monthLabel}</strong>
              {sk.skipped
                ? ' (skipped — month already present behind the front)'
                : <> — {sk.channelCount} channels, AdSense {fmtUsd(sk.adSenseTotal)} · DS {fmtUsd(sk.directSoldTotal)}</>}
              {sk.sheetUrl && (
                <>
                  {' · '}
                  <a href={sk.sheetUrl} target="_blank" rel="noreferrer" style={styles.link}>Open</a>
                </>
              )}
            </>
          ) : (
            <span style={styles.failedHint}>
              Skara Payments Tracker failed — {sk.error}. Re-run it from the Step re-runs panel.
            </span>
          )}
        </div>
        {missing.length > 0 && (
          <details style={styles.mirrorDetails}>
            <summary style={styles.mirrorSummary}>
              {missing.length} roster channel{missing.length === 1 ? '' : 's'} with revenue but no tracker row — add the row(s) in column A, then re-run “Skara Payments Tracker”
            </summary>
            <ul style={styles.mirrorList}>
              {missing.map((name: string, i: number) => (
                <li key={`sk${i}`} style={styles.mirrorErrItem}>{name}</li>
              ))}
            </ul>
          </details>
        )}
      </div>
    );
  }

  if (panels.length === 0) return null;
  return <>{panels}</>;
};

const styles: Record<string, React.CSSProperties> = {
  mirrorPanel: {
    marginTop: '8px',
    padding: '8px 12px',
    borderRadius: '6px',
    backgroundColor: 'var(--bg-surface-3)',
    border: '1px solid var(--border-1)',
  },
  mirrorHeader: {
    fontSize: '11px',
    color: 'var(--fg-2)',
  },
  mirrorDetails: {
    marginTop: '6px',
  },
  mirrorSummary: {
    fontSize: '11px',
    color: 'var(--danger)',
    cursor: 'pointer',
    fontWeight: 500,
  },
  mirrorList: {
    margin: '6px 0 0 0',
    paddingLeft: '18px',
    maxHeight: '160px',
    overflowY: 'auto',
  },
  mirrorErrItem: {
    fontSize: '11px',
    color: 'var(--fg-2)',
    marginBottom: '2px',
  },
  failedHint: {
    fontSize: '10px',
    color: 'var(--danger)',
    fontWeight: 500,
  },
  link: { color: 'var(--accent)' },
};

export default PipelinePayoutResults;
